import { useEffect, useState } from 'react';
import { BUILDER_SIZES, SHEET_SIZES, customProduct, tentPieceSize, savedTemplateType, type BuilderSize, type CalendarKind, type SavedTemplate } from '@cal/engine';
import { get, post, errorMessage } from '../api';
import { navigate } from '../router';
import { Check, Field, Modal, NumberField, Seg } from '../ui/controls';
import { toast } from '../ui/toast';
import { useLibrary } from '../library';
import { useEditor } from '../editor/store';
import { SavedTemplatePreview } from '../editor/Wireframe';
import type { Shared } from './NewCalendar';
import { HolidayOffer, createBlocker } from '../ui/HolidayOffer';

/** A labelled group of controls (not a <label>, which would capture clicks and names of the controls inside). */
function Group({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="field" role="group" aria-label={label}>
      <span className="lbl">{label}</span>
      {children}
      {hint && <span className="faint small">{hint}</span>}
    </div>
  );
}

interface SavedRow { id: string; name: string; description: string; data: SavedTemplate }

const KINDS: { value: CalendarKind; label: string; pages: string }[] = [
  { value: 'year', label: 'Year view', pages: '1 page with all 12 months' },
  { value: 'half', label: 'Tent (folded, stands on a desk)', pages: 'DL only: landscape tent or portrait tower' },
  { value: 'monthly', label: 'Monthly view', pages: '12 pages, one per month' },
];
type DlTent = 'dl-landscape' | 'dl-portrait-6' | 'dl-portrait-4';
const DL_TENTS: { value: DlTent; label: string; detail: string }[] = [
  { value: 'dl-landscape', label: 'Landscape tent, 6 months per face', detail: 'Jul–Dec (upside down) / Jan–Jun / base with other information' },
  { value: 'dl-portrait-6', label: 'Portrait tower, 6 months', detail: 'Jan–Jun | Jul–Dec | side panel with other information' },
  { value: 'dl-portrait-4', label: 'Portrait tower, 4 months', detail: 'Jan–Apr | May–Aug | Sep–Dec' },
];
const TYPE_LABEL = { year: 'Year view', tent: 'Tent', monthly: 'Monthly' } as const;

/**
 * Custom calendar builder, step 1: size, orientation, calendar type and layout
 * grid — or start from a custom template saved earlier. The design itself is
 * built in the editor (Design stage) with blocks on the layout grid.
 */
export function CustomBuilder({ onClose, start, shared, mode }: { onClose: () => void; start: React.ReactNode; shared: Shared; mode: 'saved' | 'blank' }) {
  const sets = useLibrary((s) => s.eventSets);
  const loadSets = useLibrary((s) => s.loadEventSets);
  const [saved, setSaved] = useState<SavedRow[]>([]);
  const [f, setF] = useState({ size: 'A4' as BuilderSize, orientation: 'portrait' as 'portrait' | 'landscape', kind: 'monthly' as CalendarKind, tent: 'dl-landscape' as DlTent, cover: false, cols: 6, rows: 8, savedId: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { void loadSets(); get<{ custom: SavedRow[] }>('/api/templates').then((r) => setSaved(r.custom.filter((t) => t.data?.product))).catch(() => undefined); }, [loadSets]);
  const yearSets = sets.filter((s) => s.year === shared.year || s.year === null);
  const publicSets = yearSets.filter((s) => s.kind === 'public').map((s) => s.id);
  const s = SHEET_SIZES[f.size];
  const page = f.orientation === 'portrait' ? { w: s.w, h: s.h } : { w: s.h, h: s.w };
  const chosen = mode === 'saved' ? saved.find((t) => t.id === f.savedId) : undefined;
  const blocker = createBlocker(shared) ?? (mode === 'saved' && !chosen ? 'Choose one of your templates' : null);
  const dlTent = f.kind === 'half' && f.size === 'DL';

  const create = async () => {
    setBusy(true);
    try {
      const body = chosen
        ? { name: shared.name, year: shared.year, savedTemplateId: chosen.id, eventSetIds: publicSets }
        : (() => {
          const { templateId, product } = customProduct({ size: f.size, orientation: f.orientation, kind: f.kind, cover: f.cover, tent: f.tent });
          return { name: shared.name, year: shared.year, templateId, product, layoutGrid: { cols: f.cols, rows: f.rows, show: true, snap: true }, eventSetIds: publicSets };
        })();
      const r = await post<{ id: string }>('/api/projects', body);
      useEditor.setState({ stage: 2 });
      navigate(`/projects/${r.id}`);
    } catch (e) { toast(errorMessage(e), 'err'); setBusy(false); }
  };

  return (
    <Modal wide title="New calendar" onClose={onClose} footer={<>{blocker && <span className="small muted grow">{blocker}</span>}<button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !!blocker} onClick={create}>{busy ? 'Creating…' : mode === 'saved' ? 'Create and open' : 'Create and open the builder'}</button></>}>
      {start}
      <div className="grid2">
        <Field label="Project name"><input className="input" value={shared.name} placeholder={`e.g. Coastal Plumbing desk tent ${shared.year}`} onChange={(e) => shared.setName(e.target.value)} autoFocus /></Field>
        <Field label="Year"><input className="input" type="number" min={1900} max={2100} value={shared.year} onChange={(e) => shared.setYear(+e.target.value)} /></Field>
      </div>

      {mode === 'saved' && (saved.length === 0
        ? <p className="small muted" style={{ margin: 0 }}>No templates yet. Build a design, then use “Save this design as a template” in 2 · Design.</p>
        : (
        <Group label="My templates">
          <div className="tpl-grid">
            {saved.map((t) => (
              <button key={t.id} type="button" className="tpl-card" aria-pressed={f.savedId === t.id} onClick={() => setF((p) => ({ ...p, savedId: t.id }))} title={t.description}>
                <SavedTemplatePreview t={t.data} width={100} />
                <span className="small" style={{ fontWeight: 600 }}>{t.name}</span>
                <span className="small faint">{TYPE_LABEL[savedTemplateType(t.data)]} · {Math.round(t.data.size.w)} × {Math.round(t.data.size.h)} mm</span>
              </button>
            ))}
          </div>
        </Group>
      ))}

      {mode === 'blank' && (
        <>
          <div className="grid2">
            <Group label="1 · Page size">
              <div className="row wrap" style={{ gap: 6 }}>
                {BUILDER_SIZES.map((x) => <button key={x} type="button" className={`btn sm ${f.size === x ? 'active' : ''}`} aria-pressed={f.size === x} onClick={() => setF((p) => ({ ...p, size: x, kind: p.kind === 'half' && x !== 'DL' ? 'monthly' : p.kind }))}>{x}</button>)}
              </div>
            </Group>
            <Group label="2 · Orientation">
              {dlTent
                ? <span className="small faint">Set by the DL tent style below.</span>
                : <Seg label="Orientation" value={f.orientation} onChange={(v) => setF((p) => ({ ...p, orientation: v }))} options={[{ value: 'portrait', label: 'Portrait' }, { value: 'landscape', label: 'Landscape' }]} />}
            </Group>
          </div>
          <Group label="3 · Calendar type">
            <div className="col" style={{ gap: 4 }} role="radiogroup" aria-label="Calendar type">
              {KINDS.map((k) => (
                <label key={k.value} className="check" title={k.value === 'half' && f.size !== 'DL' ? 'Choose the DL page size for a tent calendar (A4 tent cards are in the product catalogue)' : undefined}>
                  <input type="radio" name="kind" disabled={k.value === 'half' && f.size !== 'DL'} checked={f.kind === k.value} onChange={() => setF((p) => ({ ...p, kind: k.value }))} />
                  <span><strong>{k.label}</strong> <span className="small faint">— {k.pages}</span></span>
                </label>
              ))}
            </div>
          </Group>
          {f.kind === 'monthly' && <Check label="Add a cover page (13 pages)" checked={f.cover} onChange={(v) => setF((p) => ({ ...p, cover: v }))} />}
          {dlTent && (
            <Group label="DL tent style" hint="Three 99 mm panels and a 15 mm tape strip (left blank). Bleed 3 mm, safe area 3 mm from trim and folds, no crop or fold marks. SRA3 2-up.">
              <div className="col" style={{ gap: 4 }} role="radiogroup" aria-label="DL tent style">
                {DL_TENTS.map((t) => (
                  <label key={t.value} className="check">
                    <input type="radio" name="dltent" checked={f.tent === t.value} onChange={() => setF((p) => ({ ...p, tent: t.value }))} />
                    <span><strong>{t.label}</strong> <span className="small faint">— {t.detail}</span></span>
                  </label>
                ))}
              </div>
            </Group>
          )}
          <p className="small muted" style={{ margin: 0 }}>
            {dlTent ? (() => {
              const { product } = customProduct({ size: f.size, orientation: f.orientation, kind: f.kind, cover: false, tent: f.tent });
              const piece = tentPieceSize({ widthMm: product.widthMm!, heightMm: product.heightMm!, tent: product.tent!, bleedMm: product.bleedMm ?? 3 });
              return `Flat sheet ${product.widthMm} × ${product.heightMm} mm trimmed (${piece.w} × ${piece.h} mm with bleed).`;
            })()
              : `${page.w} × ${page.h} mm finished size.`}
          </p>
          <Group label="4 · Layout grid" hint="Blocks snap to whole cells. Change it any time in the editor (click the page background).">
            <div className="grid2">
              <NumberField label="Columns" value={f.cols} min={1} max={48} step={1} digits={0} onChange={(v) => setF((p) => ({ ...p, cols: Math.round(v) }))} />
              <NumberField label="Rows" value={f.rows} min={1} max={48} step={1} digits={0} onChange={(v) => setF((p) => ({ ...p, rows: Math.round(v) }))} />
            </div>
          </Group>
        </>
      )}
      {publicSets.length
        ? <p className="small faint" style={{ margin: 0 }}>Public holidays for {shared.year} are added automatically; manage holidays later under Content.</p>
        : <HolidayOffer year={shared.year} />}
    </Modal>
  );
}
